import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Camera, ChevronRight, Compass, ImagePlus, Lock, ShieldCheck, Users, X } from 'lucide-react';
import type { CommunityCategory, Member, Space, SpaceKind } from '@spawnwire/shared';
import { useImageCropper } from '../../components/ImageCropper';
import { Button, clsx, Field, Input, Modal, TextArea } from '../../components/ui';
import { errorMessage, http, uploadFile } from '../../lib/api';
import { CATEGORIES, CATEGORY_META } from '../../lib/discover';
import { actions, useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { SpaceIcon } from '../shell/SpaceIcon';

const IMAGE_TYPES = 'image/png,image/jpeg,image/gif,image/webp,image/avif';

/** A picked image and a preview URL for it, released when it changes or the dialog closes. */
function usePickedImage() {
  const [file, setFile] = useState<File | null>(null);
  const url = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => void (url && URL.revokeObjectURL(url)), [url]);
  return { file, url, setFile };
}

/** Takes the chosen file and clears the input, so picking the same file again still fires. */
function pick(input: HTMLInputElement, set: (f: File) => void) {
  const file = input.files?.[0];
  input.value = '';
  if (file) set(file);
}

function ChoiceCard({ icon: Icon, title, body, onClick, tone }: { icon: typeof Users; title: string; body: string; onClick: () => void; tone: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-4 rounded-xl border border-line bg-sidebar p-4 text-left transition-colors hover:border-line-strong hover:bg-elevated focus-visible:outline-2 focus-visible:outline-accent"
    >
      <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl text-white" style={{ background: tone }}>
        <Icon className="size-6" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-fg">{title}</span>
        <span className="mt-0.5 block text-sm text-muted">{body}</span>
      </span>
      <ChevronRight className="size-5 text-faint transition-transform group-hover:translate-x-0.5 group-hover:text-fg" aria-hidden />
    </button>
  );
}

type Step = 'kind' | 'details' | 'community';

/**
 * Create a space, Discord style: pick "for friends" or "for a community", then a name, icon and banner.
 * Communities also pick a category and whether to apply to Discover (which staff review before listing).
 */
export function CreateSpaceDialog() {
  const navigate = useNavigate();
  const me = useStore((s) => s.me);
  const [step, setStep] = useState<Step>('kind');
  const [kind, setKind] = useState<SpaceKind>('friends');
  const [name, setName] = useState(`${me?.displayName ?? 'My'}'s space`);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<CommunityCategory | null>(null);
  const [submit, setSubmit] = useState<boolean | null>(null);
  const icon = usePickedImage();
  const banner = usePickedImage();
  const iconCropper = useImageCropper('icon', icon.setFile);
  const bannerCropper = useImageCropper('banner', banner.setFile);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const community = kind === 'community';
  const detailsValid = name.trim().length >= 2 && (!community || description.trim().length === 0 || description.trim().length >= 10);
  const communityValid = !!category && submit !== null && (!submit || description.trim().length >= 10);

  function choose(next: SpaceKind) {
    setKind(next);
    setName(next === 'community' ? `${me?.displayName ?? 'My'}'s community` : `${me?.displayName ?? 'My'}'s space`);
    setStep('details');
  }

  async function create(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { space, member } = await http.post<{ space: Space; member: Member }>('/spaces', {
        name: name.trim(),
        kind,
        ...(community ? { category, submitToDiscover: !!submit, description: description.trim() || undefined } : {}),
      });
      actions.upsertSpace(space, member);
      // The space exists now; a failed image upload shouldn't lose it, so report and carry on.
      for (const [file, path] of [
        [icon.file, 'icon'],
        [banner.file, 'banner'],
      ] as const) {
        if (!file) continue;
        await uploadFile(`/spaces/${space.id}/${path}`, file).promise.catch((err) => ui.toast(`Couldn't upload the ${path}: ${errorMessage(err)}`, 'error'));
      }
      ui.close();
      if (community && submit) ui.toast('Your community has been submitted and is under review. Check your DMs for updates.', 'success');
      navigate(`/app/${space.id}`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  const title = step === 'kind' ? 'Create a space' : step === 'details' ? 'Customize your space' : 'Tell us about your community';
  const description_ =
    step === 'kind'
      ? 'Your space is where you and your people hang out. Who is it for?'
      : step === 'details'
        ? 'Give it a personality with a name, an icon and a banner. You can change these any time.'
        : 'This helps people find it. Communities only appear on Discover after Spawnwire staff approve them.';

  const back = (
    <Button variant="ghost" onClick={() => setStep(step === 'community' ? 'details' : 'kind')} disabled={busy}>
      <ArrowLeft className="size-4" aria-hidden /> Back
    </Button>
  );

  return (
    <Modal
      open
      onClose={ui.close}
      title={title}
      description={description_}
      size={step === 'kind' ? 'md' : 'lg'}
      footer={
        step === 'kind' ? undefined : step === 'details' ? (
          <>
            {back}
            <div className="flex-1" />
            {community ? (
              <Button onClick={() => setStep('community')} disabled={!detailsValid}>
                Next
              </Button>
            ) : (
              <Button loading={busy} onClick={() => void create()} disabled={!detailsValid}>
                Create space
              </Button>
            )}
          </>
        ) : (
          <>
            {back}
            <div className="flex-1" />
            <Button loading={busy} onClick={() => void create()} disabled={!communityValid}>
              {submit ? 'Create and submit' : 'Create community'}
            </Button>
          </>
        )
      }
    >
      {step === 'kind' && (
        <div className="space-y-3">
          <ChoiceCard icon={Lock} tone="#3ba55d" title="For me and my friends" body="A private hangout. Only people you invite can join." onClick={() => choose('friends')} />
          <ChoiceCard icon={Users} tone="#5865f2" title="For a club or community" body="Pick a topic and apply to be listed on Discover so new people can find you." onClick={() => choose('community')} />
          <div className="pt-3 text-center">
            <p className="text-sm font-semibold text-fg">Have an invite already?</p>
            <Button variant="secondary" className="mt-2 w-full" onClick={() => ui.open({ kind: 'joinSpace' })}>
              Join a space
            </Button>
          </div>
        </div>
      )}

      {step === 'details' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!detailsValid) return;
            if (community) setStep('community');
            else void create();
          }}
          className="space-y-4"
        >
          {/* Banner with the icon overlapping it, like the space's own header. */}
          <div className="relative pb-10">
            <label className="group relative block aspect-[3/1] cursor-pointer overflow-hidden rounded-xl border border-dashed border-line-strong bg-sidebar">
              {banner.url ? (
                <img src={banner.url} alt="" className="size-full object-cover" />
              ) : (
                <span className="flex size-full flex-col items-center justify-center gap-1 text-sm text-muted">
                  <ImagePlus className="size-6" aria-hidden />
                  Upload a banner
                </span>
              )}
              <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-sm font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                {banner.url ? 'Change banner' : 'Choose an image'}
              </span>
              <input type="file" accept={IMAGE_TYPES} className="sr-only" aria-label="Banner image" onChange={(e) => pick(e.target, bannerCropper.pick)} />
            </label>
            {banner.url && (
              <button type="button" aria-label="Remove banner" onClick={() => banner.setFile(null)} className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white hover:bg-black/80">
                <X className="size-4" />
              </button>
            )}
            <label className="group absolute bottom-0 left-4 cursor-pointer rounded-[30%] border-4 border-overlay bg-overlay">
              {icon.url ? (
                <img src={icon.url} alt="" className="size-20 rounded-[30%] object-cover" />
              ) : (
                <span className="relative block">
                  <SpaceIcon name={name || '?'} iconUrl={null} size={80} active />
                </span>
              )}
              <span className="absolute inset-0 flex items-center justify-center rounded-[30%] bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100">
                <Camera className="size-6" aria-hidden />
              </span>
              <input type="file" accept={IMAGE_TYPES} className="sr-only" aria-label="Space icon" onChange={(e) => pick(e.target, iconCropper.pick)} />
            </label>
            {icon.url && (
              <button type="button" onClick={() => icon.setFile(null)} className="absolute bottom-1 left-28 text-xs text-muted hover:text-fg hover:underline">
                Remove icon
              </button>
            )}
            {iconCropper.dialog}
            {bannerCropper.dialog}
          </div>
          <Field label={community ? 'Community name' : 'Space name'} htmlFor="space-name">
            <Input id="space-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} autoFocus />
          </Field>
          {community && (
            <Field label="What is your community about?" htmlFor="space-desc" hint="Shown on your Discover card. Needed if you apply to Discover.">
              <TextArea id="space-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} placeholder="Weekly game nights, tournaments and a friendly crowd." />
            </Field>
          )}
          {!community && <p className="flex items-center gap-2 text-xs text-faint"><Lock className="size-3.5" aria-hidden /> Friends spaces are private and never appear on Discover.</p>}
          <button type="submit" hidden />
        </form>
      )}

      {step === 'community' && (
        <div className="space-y-6">
          <fieldset>
            <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Category</legend>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {CATEGORIES.map((c) => {
                const meta = CATEGORY_META[c];
                const Icon = meta.icon;
                const on = category === c;
                return (
                  <button
                    key={c}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setCategory(c)}
                    className={clsx('flex flex-col items-start gap-2 rounded-xl border p-3 text-left transition-colors', on ? 'border-accent bg-accent/10' : 'border-line bg-sidebar hover:bg-elevated')}
                  >
                    <span className="flex size-9 items-center justify-center rounded-lg text-white" style={{ background: meta.color }}>
                      <Icon className="size-5" aria-hidden />
                    </span>
                    <span className="text-sm font-semibold text-fg">{meta.label}</span>
                    <span className="text-xs leading-snug text-muted">{meta.blurb}</span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Submit your community to Discover?</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                { value: true, icon: Compass, title: 'Yes, submit it', body: 'Spawnwire staff review it. Once approved, anyone can find and join it on Discover.' },
                { value: false, icon: Lock, title: 'Not now', body: 'Only people you invite can join. You can apply later from Space settings.' },
              ].map((o) => (
                <button
                  key={String(o.value)}
                  type="button"
                  aria-pressed={submit === o.value}
                  onClick={() => setSubmit(o.value)}
                  className={clsx('flex items-start gap-3 rounded-xl border p-3 text-left transition-colors', submit === o.value ? 'border-accent bg-accent/10' : 'border-line bg-sidebar hover:bg-elevated')}
                >
                  <o.icon className={clsx('mt-0.5 size-5 shrink-0', submit === o.value ? 'text-accent' : 'text-muted')} aria-hidden />
                  <span>
                    <span className="block text-sm font-semibold text-fg">{o.title}</span>
                    <span className="mt-0.5 block text-xs leading-snug text-muted">{o.body}</span>
                  </span>
                </button>
              ))}
            </div>
            {submit && description.trim().length < 10 && (
              <p className="mt-2 text-sm text-warning">
                Go back and describe what your community is about (at least 10 characters) so reviewers and members know.
              </p>
            )}
            {submit && (
              <p className="mt-3 flex items-start gap-2 rounded-lg bg-sidebar p-3 text-xs text-muted">
                <ShieldCheck className="size-4 shrink-0 text-success" aria-hidden />
                Pending and denied communities never appear on Discover. We'll send you a DM from Spawnwire when it's submitted and again when it's reviewed.
              </p>
            )}
          </fieldset>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-3 rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
          {error}
        </p>
      )}
    </Modal>
  );
}
